/* --------------------------------------------------------------------------
 * Single source of truth for everything on the site.
 * Update copy, projects and links here components just render this.
 * ----------------------------------------------------------------------- */

export const profile = {
  name: 'Dikshit Patoliya',
  role: 'Software Engineer React Native',
  location: 'Surat, India',
  availability: 'Available for new projects',
  email: 'patoliyadikshit17@gmail.com',
  phone: '+91 9879439697',
  cv: 'Dikshit_Patoliya_CV.pdf',
  photo: 'img/dikshit-portrait.png',
  avatar: 'img/dikshit-avatar.png',
  linkedin: 'https://in.linkedin.com/in/dikshitpatoliya',
  summary:
    'Software Engineer with 5+ years building and shipping React Native applications in TypeScript across iOS and Android. I have shipped 28+ production apps end to end UI, API integration, automated testing, performance and store deployment and I now build AI-native product experiences on top of OpenAI, Claude and Gemini.',
};

export const stats = [
  { value: '5+',  label: 'Years shipping React Native' },
  { value: '28+', label: 'Production apps released' },
  { value: 'iOS + Android', label: 'Shipped on both stores' },
  { value: 'US · CA · EU', label: 'Remote teams served' },
];

/* Rotating words in the hero headline */
export const heroRotation = [
  'that ship.',
  'people keep.',
  'stores approve.',
  'teams trust.',
];

export const marquee = [
  'React Native', 'TypeScript', 'Expo', 'React.js', 'Redux Toolkit', 'Redux Saga',
  'OpenAI', 'Claude', 'Gemini', 'RAG', 'Vector DB', 'AI Agents',
  'Computer Vision', 'OCR', 'Speech-to-Text', 'GraphQL', 'REST API',
  'Firebase', 'Push Notifications', 'Stripe', 'App Store', 'Play Store',
];

export const services = [
  {
    tag: 'AI',
    title: 'AI-native mobile features',
    body:
      'OpenAI, Claude and Gemini wired into real product flows generated content, personalised recommendations, RAG over your own data, and agent-style automation. Including the prompt construction, response handling and failure paths.',
    points: ['LLM & RAG integration', 'Prompt engineering', 'AI agents & automation'],
  },
  {
    tag: 'MOBILE',
    title: 'React Native product development',
    body:
      'End-to-end iOS and Android apps in React Native + TypeScript. Architecture, reusable component systems, navigation, state management and responsive UI that holds up on both platforms.',
    points: ['React Native + TypeScript', 'Expo & bare workflow', 'Reusable design systems'],
  },
  {
    tag: 'VISION',
    title: 'Computer vision & media',
    body:
      'Camera and media-heavy experiences: computer-vision analysis pipelines, OCR, speech-to-text and text-to-speech, plus audio and video playback tuned for smooth performance on device.',
    points: ['CV analysis pipelines', 'OCR & speech', 'Audio / video playback'],
  },
  {
    tag: 'SHIP',
    title: 'Release, scale & support',
    body:
      'The unglamorous half that decides whether an app succeeds: API and payment integration, push notifications, automated tests, performance profiling, and getting through App Store and Play review.',
    points: ['Payments & push', 'Automated testing', 'Store deployment'],
  },
];

/* Each platform carries its own store URL so the iOS / Android / Web button
   is the link itself. `href: null` marks a platform that shipped but has no
   public listing (delisted, or web behind a client login). */
export const projects = [
  {
    title: 'Bammby',
    subtitle: 'Smart Dating & Matches',
    tag: 'AI · Dating',
    image: null,
    icon: 'img/apps/bammby.png',
    accent: ['#FF6FB1', '#7C5CFF'],
    glyph: '♥',
    body:
      'AI-powered dating app built in React Native + TypeScript. I integrated the OpenAI API to generate personalised user bios and suggest relevant interests, and built the prompt construction, response handling and AI-content presentation inside the app.',
    highlights: ['OpenAI-generated bios', 'Interest suggestion engine', 'Profile, discovery & matching flows'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/in/app/bammby/id6450325156' },
      { name: 'Android', href: 'https://play.google.com/store/apps/details?id=com.bammby.app' },
    ],
  },
  {
    title: 'Couro Run',
    subtitle: 'AI running-form analysis',
    year: '2025',
    tag: 'AI · Computer Vision',
    image: null,
    icon: 'img/apps/couro-run.png',
    accent: ['#22D3EE', '#7C5CFF'],
    glyph: '◔',
    body:
      'Mobile workflows for an AI-powered running analysis platform that uses computer vision to evaluate running form. I integrated the analysis pipeline and API-driven results into the app and built the responsive UI for presenting personalised insights and recommendations.',
    highlights: ['Computer-vision analysis workflow', 'Personalised insight UI', 'Cross-team product delivery'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/us/app/couro-run/id6737208894' },
      { name: 'Web', href: 'https://couro.io/' },
    ],
  },
  {
    title: 'TinyPal',
    subtitle: 'Parenting assistant',
    year: '2025',
    tag: 'AI · Parenting',
    image: null,
    icon: 'img/apps/tinypal.png',
    accent: ['#8BE8CB', '#22D3EE'],
    glyph: '✦',
    body:
      'Parenting assistant app that puts AI-guided answers and day-to-day child development guidance in reach for new parents. Shipped to both stores in React Native, with the conversational assistant and tracking flows wired into the product.',
    highlights: ['AI-assisted parenting guidance', 'Child development tracking', 'iOS + Android release'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/us/app/parenting-app-tinypal/id6739141430' },
      { name: 'Android', href: 'https://play.google.com/store/apps/details?id=com.tinypal.pr' },
    ],
  },
  {
    title: 'AudioMe',
    subtitle: 'Audiobooks & Ebooks',
    tag: 'Media',
    image: null,
    icon: 'img/apps/audiome.png',
    accent: ['#7C5CFF', '#22D3EE'],
    glyph: '♫',
    body:
      'Cross-platform audiobook and e-book app in React Native + TypeScript. Audio playback, content discovery and interactive reading across iOS and Android, with state management for content sync and navigation.',
    highlights: ['Background audio playback', 'Content discovery & reading', 'Performance-tuned UI'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/us/app/audiome-inc/id6751253460' },
      { name: 'Android', href: 'https://play.google.com/store/apps/details?id=com.audiome.project' },
    ],
  },
  {
    title: 'The School Of Breath',
    subtitle: 'Meditation & breathwork',
    tag: 'Wellness',
    image: null,
    icon: 'img/apps/school-of-breath.png',
    accent: ['#22D3EE', '#8BE8CB'],
    glyph: '◎',
    body:
      'Cross-platform wellness app built with Expo, React Native and TypeScript. Video courses, guided meditation, breathwork sessions and sleep music, with backend APIs managing course, video, audio and user data.',
    highlights: ['Video & guided-session player', 'Expo cross-platform build', 'Content-heavy navigation'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/us/app/the-school-of-breath/id6736984340' },
      { name: 'Android', href: 'https://play.google.com/store/apps/details?id=com.meditatewithabhi.theschoolofbreath' },
    ],
  },
  {
    title: 'Balmukund Super Partner',
    subtitle: 'Dealer management system',
    tag: 'B2B',
    image: null,
    icon: 'img/apps/balmukund.png',
    accent: ['#FFB86B', '#FF6FB1'],
    glyph: '▤',
    body:
      'React Native + TypeScript dealer management system for Balmukund Super Steel dealers and distributors a centralised platform for managing business operations, offers, schemes and business updates.',
    highlights: ['Centralised dealer operations', 'Offers & schemes module', 'Data-driven workflows'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/in/app/balmukund-super-partner/id6741348607' },
      { name: 'Android', href: 'https://play.google.com/store/apps/details?id=com.balmukund' },
    ],
  },
];

/* Smaller shipped work shown as a compact grid */
export const alsoShipped = [
  {
    title: 'Swob',
    note: 'Swipe-based job search connecting candidates with local employers, on iOS and Android.',
    icon: 'img/apps/swob.png',
    accent: ['#7C5CFF', '#22D3EE'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/us/app/swob-job-search-app/id1293871483' },
      { name: 'Android', href: 'https://play.google.com/store/apps/details?id=com.swobapp.swob' },
    ],
  },
  {
    title: 'InovCares Providers',
    note: 'Provider-side app for a connected care platform patient interactions and clinical workflows on mobile.',
    icon: 'img/apps/inovcares.png',
    accent: ['#22D3EE', '#8BE8CB'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/in/app/inovcares-providers/id1515456403' },
      { name: 'Android', href: 'https://play.google.com/store/apps/details?id=com.inovcares.physician' },
    ],
  },
  {
    title: 'Baz Performance',
    note: 'Training and performance app for athletes programme delivery, session tracking and progress.',
    icon: 'img/apps/baz-performance.png',
    accent: ['#FFB86B', '#FF6FB1'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/ca/app/baz-performance/id6499425244' },
      { name: 'Android', href: 'https://play.google.com/store/apps/details?id=com.bazperformanceapp' },
    ],
  },
  {
    title: 'Kupenda',
    note: 'Dating and social app pairing profile discovery with puzzle-based ice-breakers.',
    icon: 'img/apps/kupenda.png',
    accent: ['#FF6FB1', '#7C5CFF'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/in/app/kupenda-conquer-loneliness/id1569230868' },
      { name: 'Android', href: 'https://play.google.com/store/apps/details?id=com.kupenda' },
    ],
  },
  {
    title: 'Billiant: AI Bill Tracker',
    note: 'Detects bills straight from your inbox, categorises them and reminds you before they are due.',
    icon: 'img/apps/billiant.png',
    accent: ['#8BE8CB', '#22D3EE'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/us/app/billiant-ai-bill-tracker/id1457021397' },
    ],
  },
  {
    title: 'Everle',
    note: 'Social networking app shipped to the App Store, built in React Native.',
    icon: 'img/apps/everle.png',
    accent: ['#7C5CFF', '#FF6FB1'],
    platforms: [
      { name: 'iOS', href: 'https://apps.apple.com/us/app/everle/id6453520044' },
    ],
  },
];

export const experience = [
  {
    company: 'Couro LLC',
    where: 'US · Remote',
    role: 'Mobile & Web Application Developer',
    period: 'Jan 2025 — Present',
    current: true,
    points: [
      'Ship React Native features in TypeScript across iOS and Android.',
      'Build mobile workflows for an AI-powered running analysis platform using computer vision.',
      'Integrate API-driven data and backend services, translating complex requirements into mobile workflows.',
      'Develop reusable UI components for personalised insights and recommendations.',
    ],
  },
  {
    company: 'SnapCycle',
    where: 'Canada · Remote',
    role: 'Mobile Application Developer',
    period: 'Sept 2024 — Jan 2025',
    points: [
      'Owned the React Native codebase and led mobile feature development from requirements through release.',
      'Made architecture decisions and established reusable patterns across the application.',
      'Worked directly with international stakeholders across time zones to maintain release timelines.',
      'Diagnosed critical production bugs and performance bottlenecks, improving stability release over release.',
    ],
  },
  {
    company: 'Madhav Technologies',
    where: 'Surat, India',
    role: 'Senior React Native Developer',
    period: 'Sept 2022 — Dec 2023',
    points: [
      'Built and shipped 6+ React Native apps from scratch in TypeScript, through to production deployment.',
      'Owned features across the full lifecycle, from requirements analysis to release.',
      'Developed reusable components and mobile interfaces focused on maintainability and responsive UI.',
      'Integrated REST APIs and third-party services to support data-driven workflows.',
    ],
  },
  {
    company: 'Tagline Infotech',
    where: 'Surat, India',
    role: 'Junior React Native Developer',
    period: 'Feb 2022 — Aug 2022',
    points: [
      'Developed and maintained React Native apps using JavaScript/TypeScript, Redux and REST APIs.',
      'Implemented reusable UI components, navigation flows and application state management.',
      'Handled asynchronous data flows with a Redux-based architecture.',
    ],
  },
];

export const education = {
  school: 'Veer Narmad South Gujarat University, Surat',
  degree: 'Bachelor of Computer Applications',
  period: '2019 — 2022',
};

export const stack = [
  {
    group: 'Mobile',
    items: ['React Native', 'Expo', 'TypeScript', 'JavaScript', 'React.js', 'Redux Toolkit', 'Redux Saga', 'Responsive UI'],
  },
  {
    group: 'AI & LLM',
    items: ['OpenAI API', 'Claude API', 'Gemini API', 'Prompt Engineering', 'Generative AI', 'RAG & Vector DBs', 'AI Agents', 'OCR', 'Speech-to-Text', 'Text-to-Speech'],
  },
  {
    group: 'Backend & Data',
    items: ['REST API', 'GraphQL', 'Firebase', 'Push Notifications', 'Payment Gateways', 'Third-party SDKs'],
  },
  {
    group: 'Ship & Tooling',
    items: ['App Store', 'Play Store', 'Xcode', 'Android Studio', 'Automated Testing', 'Performance Tuning', 'Cursor', 'Claude Code'],
  },
];

/* `image` may be null — the card falls back to initials on a gradient.
   `company` is optional. */
export const testimonials = [
  {
    image: null,
    name: 'Nana A.',
    company: 'AudioMe LLC',
    place: 'United States',
    quote:
      'Dikshit did an excellent job stabilizing and improving a codebase that had been negatively impacted by previous developers. His ability to quickly identify issues, implement clean…',
  },
  {
    image: 'img/Client/avatar-1.jpg',
    name: 'Neha',
    place: 'India',
    quote:
      'Dikshit is one of the best React Native developers I have hired on Upwork. If you have the opportunity to hire him do it, you will not regret it. Excellent technical skills, creative mindset, and great ability to understand requirements and implement solutions. I will definitely hire again.',
  },
  {
    image: 'img/Client/avatar-2.jpg',
    name: 'David I',
    place: 'Spain',
    quote:
      "Patoliya is one of the best freelancers on Upwork. I challenged him with a demanding and difficult task and he was able to provide a working solution within minutes. Really appreciate and love this freelancer's job. I couldn't be happier. If I could, I would give him more than 5 stars.",
  },
  {
    image: 'img/Client/avatar-4.png',
    name: 'Fredrik Pihlgren',
    place: 'Norrköping, Sweden',
    quote: 'He solved the problem for me. Recommended guy to work with!',
  },
  {
    image: 'img/Client/avatar-3.jpg',
    name: 'copata',
    place: 'Nigeria',
    quote: 'Very patient and skilled developer.',
  },
  {
    image: 'img/Client/avatar-1.jpg',
    name: 'Kishan Arava',
    place: 'Australia',
    quote: 'Works very quickly. Enjoy working with him. Will hire again.',
  },
];

export const navLinks = [
  { id: 'work',     label: 'Work' },
  { id: 'services', label: 'Services' },
  { id: 'about',    label: 'About' },
  { id: 'stack',    label: 'Stack' },
  { id: 'contact',  label: 'Contact' },
];
